import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface Reflection {
  id?: string;
  reflection_date: string;
  gratitude?: string;
  accomplishments?: string;
  challenges?: string;
  lessons?: string;
  tomorrow_priorities?: string;
  mood_rating?: number;
  energy_level?: number;
}

interface DailyReflectionProps {
  date?: string;
  onClose?: () => void;
}

const MOOD_OPTIONS = [
  { value: 1, emoji: '😢', label: 'Very Bad' },
  { value: 2, emoji: '😕', label: 'Bad' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😊', label: 'Great' }
];

const ENERGY_OPTIONS = [
  { value: 1, emoji: '🔋', label: 'Exhausted', fill: 20 },
  { value: 2, emoji: '🔋', label: 'Low', fill: 40 },
  { value: 3, emoji: '🔋', label: 'Moderate', fill: 60 },
  { value: 4, emoji: '🔋', label: 'Good', fill: 80 },
  { value: 5, emoji: '🔋', label: 'High', fill: 100 }
];

const DailyReflection: React.FC<DailyReflectionProps> = ({ date, onClose }) => {
  const currentDate = date || new Date().toISOString().split('T')[0];
  const [reflection, setReflection] = useState<Reflection>({ reflection_date: currentDate });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>('gratitude');

  useEffect(() => {
    fetchReflection();
  }, [currentDate]);

  const fetchReflection = async () => {
    setLoading(true);
    try {
      const response = await apiService.getReflection(currentDate);
      if (response.success && response.reflections) {
        setReflection({ ...response.reflections, reflection_date: currentDate });
      } else {
        setReflection({ reflection_date: currentDate });
      }
    } catch (error) {
      console.error('Failed to fetch reflection:', error);
      setReflection({ reflection_date: currentDate });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.saveReflection({
        date: currentDate,
        gratitude: reflection.gratitude,
        accomplishments: reflection.accomplishments,
        challenges: reflection.challenges,
        lessons: reflection.lessons,
        tomorrowPriorities: reflection.tomorrow_priorities,
        moodRating: reflection.mood_rating,
        energyLevel: reflection.energy_level
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save reflection:', error);
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { key: 'gratitude', title: 'Gratitude', prompt: "What are you grateful for today?", emoji: '🙏' },
    { key: 'accomplishments', title: 'Accomplishments', prompt: "What did you accomplish?", emoji: '🏆' },
    { key: 'challenges', title: 'Challenges', prompt: "What challenges did you face?", emoji: '💪' },
    { key: 'lessons', title: 'Lessons Learned', prompt: "What did you learn today?", emoji: '💡' },
    { key: 'tomorrow_priorities', title: "Tomorrow's Priorities", prompt: "What are your top priorities for tomorrow?", emoji: '🎯' }
  ];

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '24px', 
        borderRadius: '16px 16px 0 0',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Daily Reflection</h2>
            <p style={{ margin: '4px 0 0 0', opacity: 0.9 }}>
              {new Date(currentDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '0 0 16px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        {/* Usage Tips */}
        <div style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f5f3ff', 
          borderBottom: '1px solid #e5e7eb'
        }}>
          <p style={{ margin: 0, color: '#6b21a8', fontSize: '0.8rem', lineHeight: 1.5 }}>
            Take 5 minutes to reflect on your day. Write about what went well, what was challenging, and what you learned. 
            Regular reflection helps build self-awareness and improves future decision-making.
          </p>
        </div>
        {/* Mood & Energy */}
        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Mood */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                How are you feeling?
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {MOOD_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setReflection({ ...reflection, mood_rating: option.value })}
                    title={option.label}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: reflection.mood_rating === option.value ? '2px solid #667eea' : '2px solid #e5e7eb',
                      borderRadius: '10px',
                      backgroundColor: reflection.mood_rating === option.value ? '#f0f4ff' : 'white',
                      cursor: 'pointer',
                      fontSize: '1.5rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    {option.emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Energy */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                Energy Level
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {ENERGY_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setReflection({ ...reflection, energy_level: option.value })}
                    title={option.label}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: reflection.energy_level === option.value ? '2px solid #22c55e' : '2px solid #e5e7eb',
                      borderRadius: '10px',
                      backgroundColor: reflection.energy_level === option.value ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '12px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      margin: '0 auto'
                    }}>
                      <div style={{
                        width: `${option.fill}%`,
                        height: '100%',
                        backgroundColor: option.fill > 60 ? '#22c55e' : option.fill > 30 ? '#f59e0b' : '#ef4444'
                      }} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reflection Sections */}
        <div style={{ padding: '24px' }}>
          {sections.map(section => (
            <div key={section.key} style={{ marginBottom: '20px' }}>
              <button
                onClick={() => setActiveSection(activeSection === section.key ? null : section.key)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: activeSection === section.key ? '#f0f4ff' : '#f9fafb',
                  border: 'none',
                  borderRadius: activeSection === section.key ? '10px 10px 0 0' : '10px',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '1.25rem' }}>{section.emoji}</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#374151' }}>{section.title}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{section.prompt}</div>
                  </div>
                </div>
                <span style={{ 
                  color: '#9ca3af', 
                  transform: activeSection === section.key ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }}>
                  ▼
                </span>
              </button>

              {activeSection === section.key && (
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0 0 10px 10px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <textarea
                    value={(reflection as any)[section.key] || ''}
                    onChange={(e) => setReflection({ ...reflection, [section.key]: e.target.value })}
                    placeholder={`Write your thoughts about ${section.title.toLowerCase()}...`}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div style={{ padding: '20px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          {saved && (
            <span style={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              ✓ Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 32px',
              background: saving ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : 'Save Reflection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyReflection;
